// ============================================
// CHÁMOMILE - Product Reviews Display Component
// Shows star rating and customer reviews on product cards
// ============================================

import React, { useState, useEffect } from 'react';
import { getProductReviews, getProductRating } from '../services/reviewService';

// Premium Gold Color Palette
const colors = {
  gold400: '#c9a962',
  gold500: '#b8943d',
  gold600: '#a07d2e',
  stone400: '#a8a29e',
  stone500: '#78716c',
  stone600: '#57534e',
};

// Star Icon
const StarIcon = ({ size = 14, filled = false }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? colors.gold500 : 'none'}
    stroke={filled ? colors.gold500 : '#e7e5e4'}
    strokeWidth="1.5"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// ============================================
// STAR RATING DISPLAY
// Shows average rating with stars
// ============================================
export const StarRatingDisplay = ({ rating, count, size = 14, showCount = true }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    }}>
      <div style={{ display: 'flex', gap: '1px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            size={size}
            filled={star <= fullStars || (star === fullStars + 1 && hasHalfStar)}
          />
        ))}
      </div>
      {showCount && count > 0 && (
        <span style={{
          fontSize: `${size - 2}px`,
          color: colors.stone500,
          marginLeft: '2px',
        }}>
          ({count})
        </span>
      )}
    </div>
  );
};

// ============================================
// PRODUCT RATING BADGE
// Compact rating display for product cards
// ============================================
export const ProductRatingBadge = ({ productId }) => {
  const [rating, setRating] = useState({ average: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRating = async () => {
      const data = await getProductRating(productId);
      setRating(data);
      setLoading(false);
    };
    loadRating();
  }, [productId]);

  if (loading || rating.count === 0) {
    return null; // Don't show anything if no reviews
  }

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      background: 'rgba(251, 191, 36, 0.1)',
      borderRadius: '999px',
      fontSize: '12px',
    }}>
      <StarIcon size={12} filled />
      <span style={{ fontWeight: 600, color: colors.gold600 }}>
        {rating.average}
      </span>
      <span style={{ color: colors.stone400, fontSize: '11px' }}>
        ({rating.count})
      </span>
    </div>
  );
};

// ============================================
// SINGLE REVIEW CARD
// Displays one customer review
// ============================================
export const ReviewCard = ({ review, compact = false }) => {
  const {
    customerName,
    rating,
    comment,
    imageUrl,
    createdAt,
    isVerifiedPurchase,
  } = review;

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-MY', { month: 'short', day: 'numeric' });
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div style={{
      padding: compact ? '12px' : '16px',
      background: '#fff',
      borderRadius: '12px',
      border: '1px solid #f5f5f4',
      marginBottom: compact ? '8px' : '12px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '10px',
      }}>
        {/* Avatar */}
        <div style={{
          width: compact ? '32px' : '40px',
          height: compact ? '32px' : '40px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${colors.gold400}, ${colors.gold500})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: compact ? '12px' : '14px',
          fontWeight: 600,
        }}>
          {getInitials(customerName)}
        </div>

        {/* Name & Rating */}
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '2px',
          }}>
            <span style={{
              fontWeight: 600,
              fontSize: compact ? '13px' : '14px',
              color: '#1c1917',
            }}>
              {customerName}
            </span>
            {isVerifiedPurchase && (
              <span style={{
                fontSize: '10px',
                padding: '2px 8px',
                background: 'rgba(180, 148, 61, 0.1)',
                color: colors.gold600,
                borderRadius: '999px',
                fontWeight: 600,
              }}>
                ✓ Verified Purchase
              </span>
            )}
          </div>
          <StarRatingDisplay rating={rating} showCount={false} size={12} />
        </div>

        {/* Date */}
        <span style={{
          fontSize: '11px',
          color: colors.stone400,
        }}>
          {formatDate(createdAt)}
        </span>
      </div>

      {/* Comment */}
      <p style={{
        fontSize: compact ? '13px' : '14px',
        color: colors.stone600,
        lineHeight: 1.5,
        margin: 0,
      }}>
        {compact && comment.length > 100
          ? `${comment.substring(0, 100)}...`
          : comment}
      </p>

      {/* Owner Response */}
      {review.response && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          background: colors.stone50,
          borderRadius: '8px',
          borderLeft: `2px solid ${colors.gold400}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: colors.gold600 }}>Owner Response</span>
            {review.responseDate && (
              <span style={{ fontSize: '10px', color: colors.stone400 }}>{formatDate(review.responseDate)}</span>
            )}
          </div>
          <p style={{ fontSize: '12px', color: colors.stone600, margin: 0, lineHeight: 1.4 }}>
            {review.response}
          </p>
        </div>
      )}

      {/* Image */}
      {imageUrl && !compact && (
        <img
          src={imageUrl}
          alt="Customer photo"
          loading="lazy"
          decoding="async"
          style={{
            width: '100%',
            maxHeight: '200px',
            objectFit: 'cover',
            borderRadius: '8px',
            marginTop: '12px',
          }}
        />
      )}

      {/* Thumbnail for compact mode */}
      {imageUrl && compact && (
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '6px',
          overflow: 'hidden',
          marginTop: '8px',
        }}>
          <img
            src={imageUrl}
            alt="Customer photo"
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>
      )}
    </div>
  );
};

// ============================================
// REVIEWS LIST
// Shows list of reviews for a product
// ============================================
export const ProductReviewsList = ({ productId, maxReviews = 5, compact = false }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState({ average: 0, count: 0 });

  useEffect(() => {
    const loadData = async () => {
      const [reviewsData, ratingData] = await Promise.all([
        getProductReviews(productId, maxReviews),
        getProductRating(productId),
      ]);
      setReviews(reviewsData);
      setRating(ratingData);
      setLoading(false);
    };
    loadData();
  }, [productId, maxReviews]);

  if (loading) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: colors.stone400,
        fontSize: '14px',
      }}>
        Loading reviews...
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: colors.stone400,
        fontSize: '14px',
      }}>
        No reviews yet. Be the first to review!
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #f5f5f4',
      }}>
        <div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#1c1917',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            {rating.average}
            <StarRatingDisplay rating={rating.average} showCount={false} size={20} />
          </div>
          <p style={{
            fontSize: '13px',
            color: colors.stone500,
            margin: '4px 0 0',
          }}>
            Based on {rating.count} review{rating.count !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Reviews */}
      {reviews.map((review) => (
        <ReviewCard key={review.id} review={review} compact={compact} />
      ))}

      {/* View More */}
      {reviews.length >= maxReviews && (
        <button style={{
          width: '100%',
          padding: '12px',
          background: 'transparent',
          border: `1px solid ${colors.stone400}`,
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: 500,
          color: colors.stone600,
          cursor: 'pointer',
          marginTop: '8px',
        }}>
          View All Reviews
        </button>
      )}
    </div>
  );
};

// ============================================
// FEATURED REVIEWS CAROUSEL
// Shows featured reviews on homepage
// ============================================
export const FeaturedReviewsCarousel = ({ reviews }) => {
  if (!reviews || reviews.length === 0) {
    return null;
  }

  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      overflowX: 'auto',
      padding: '4px 0',
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
    }}>
      {reviews.map((review) => (
        <div
          key={review.id}
          style={{
            minWidth: '280px',
            maxWidth: '280px',
            scrollSnapAlign: 'start',
          }}
        >
          <ReviewCard review={review} compact />
        </div>
      ))}
    </div>
  );
};

// ============================================
// PINTEREST-STYLE REVIEW CARD
// Image-first card design with truncation
// ============================================
export const PinterestReviewCard = ({ review, onClick }) => {
  const [expanded, setExpanded] = useState(false);
  const {
    customerName,
    rating,
    title,
    comment,
    imageUrl,
    createdAt,
    isVerifiedPurchase,
    productName,
  } = review;

  // Format name as "First L." (First name + Last initial)
  const formatName = (name) => {
    if (!name) return 'Anonymous';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1][0].toUpperCase();
    return `${firstName} ${lastInitial}.`;
  };

  // Format relative date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-MY', { month: 'short', day: 'numeric' });
  };

  const MAX_COMMENT_LENGTH = 100;
  const isLongComment = comment && comment.length > MAX_COMMENT_LENGTH;
  const displayComment = expanded || !isLongComment
    ? comment
    : `${comment.substring(0, MAX_COMMENT_LENGTH)}...`;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        cursor: onClick ? 'pointer' : 'default',
        marginBottom: '16px',
        breakInside: 'avoid',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
      }}
    >
      {/* Image - Top Position (Pinterest Style) */}
      {imageUrl && (
        <div style={{
          width: '100%',
          aspectRatio: '1',
          overflow: 'hidden',
        }}>
          <img
            src={imageUrl}
            alt="Customer review"
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>
      )}

      {/* Content - Layout: Name > Date > Stars > Comment */}
      <div style={{ padding: '14px' }}>
        {/* Name - Right below image */}
        <p style={{
          fontSize: '15px',
          fontWeight: 600,
          color: '#1c1917',
          margin: '0 0 2px',
        }}>
          {formatName(customerName)}
          {isVerifiedPurchase && (
            <span style={{
              fontSize: '10px',
              padding: '2px 8px',
              background: 'rgba(180, 148, 61, 0.1)',
              color: colors.gold600,
              borderRadius: '999px',
              fontWeight: 600,
              marginLeft: '6px',
            }}>
              ✓ Verified Purchase
            </span>
          )}
        </p>

        {/* Date - Right below name */}
        <p style={{
          fontSize: '12px',
          color: colors.stone400,
          margin: '0 0 10px',
        }}>
          {formatDate(createdAt)}
        </p>

        {/* Stars */}
        <div style={{ marginBottom: '10px' }}>
          <StarRatingDisplay rating={rating} showCount={false} size={14} />
        </div>

        {/* Product Badge (if applicable) */}
        {productName && (
          <span style={{
            display: 'inline-block',
            fontSize: '10px',
            fontWeight: 600,
            color: colors.gold600,
            background: 'rgba(180, 148, 61, 0.1)',
            padding: '3px 8px',
            borderRadius: '999px',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {productName}
          </span>
        )}

        {/* Title */}
        {title && (
          <h4 style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#1c1917',
            margin: '0 0 6px',
            lineHeight: 1.3,
          }}>
            {title}
          </h4>
        )}

        {/* Comment with "Read more" */}
        {comment && (
          <p style={{
            fontSize: '13px',
            color: colors.stone600,
            lineHeight: 1.5,
            margin: 0,
          }}>
            {displayComment}
            {isLongComment && !expanded && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.gold500,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0,
                  marginLeft: '4px',
                  fontSize: '13px',
                }}
              >
                Read more
              </button>
            )}
          </p>
        )}

        {/* Owner Response */}
        {review.response && (
          <div style={{
            marginTop: '12px',
            padding: '10px 12px',
            background: colors.stone50,
            borderRadius: '8px',
            borderLeft: `2px solid ${colors.gold400}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: colors.gold600 }}>Owner Response</span>
              {review.responseDate && (
                <span style={{ fontSize: '10px', color: colors.stone400 }}>{formatDate(review.responseDate)}</span>
              )}
            </div>
            <p style={{ fontSize: '12px', color: colors.stone600, margin: 0, lineHeight: 1.4 }}>
              {review.response}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// PINTEREST MASONRY GRID
// CSS columns-based masonry layout
// ============================================
export const PinterestMasonryGrid = ({ reviews, onReviewClick }) => {
  if (!reviews || reviews.length === 0) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: colors.stone400,
      }}>
        <p style={{ fontSize: '14px', margin: 0 }}>No reviews yet</p>
      </div>
    );
  }

  // Filter to show only reviews with images first, then others
  const reviewsWithImages = reviews.filter(r => r.imageUrl);
  const reviewsWithoutImages = reviews.filter(r => !r.imageUrl);
  const sortedReviews = [...reviewsWithImages, ...reviewsWithoutImages];

  return (
    <div style={{
      columnCount: 2,
      columnGap: '12px',
      padding: '0 4px',
    }}>
      {sortedReviews.map((review) => (
        <PinterestReviewCard
          key={review.id}
          review={review}
          onClick={onReviewClick ? () => onReviewClick(review) : undefined}
        />
      ))}
    </div>
  );
};

export default {
  StarRatingDisplay,
  ProductRatingBadge,
  ReviewCard,
  ProductReviewsList,
  FeaturedReviewsCarousel,
  PinterestReviewCard,
  PinterestMasonryGrid,
};
